import { Fragment, useState } from 'react'
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatDateRangeHuman } from '@/lib/utils'
import type { ReportsExportId } from '@/types'

/**
 * One export "section" inside the combined menu — a single backend report plus
 * an optional client-rendered PDF handler. Mirrors the props of {@link ExportButton}
 * minus the shared date range.
 */
export interface ReportExportSection {
  report: ReportsExportId
  reportLabel: string
  /** Optional extra params passed through to the CSV exporter. */
  extraParams?: Record<string, string>
  /** Optional "Print / Save as PDF" handler. Omit to hide the PDF item. */
  onPrintPdf?: () => void
}

interface ReportsExportMenuProps {
  /** One or more reports to expose under a single Export button. */
  sections: ReportExportSection[]
  fromISO: string
  toISO: string
  size?: 'sm' | 'default'
  variant?: 'outline' | 'default' | 'ghost' | 'secondary'
}

/**
 * A single Export button whose dropdown groups several reports together. Used
 * by tabs that surface more than one dataset (e.g. Tables & Parties exports
 * both "tables" and "party_size") so the user sees ONE labelled menu instead
 * of multiple identical-looking "Export" buttons.
 *
 * For tabs that export a single report, use {@link ExportButton} instead.
 */
export function ReportsExportMenu({
  sections,
  fromISO,
  toISO,
  size = 'sm',
  variant = 'outline',
}: ReportsExportMenuProps) {
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)

  const handleCsv = async (section: ReportExportSection) => {
    setBusy(true)
    try {
      await apiClient.exportReportCsv(section.report, fromISO, toISO, section.extraParams ?? {})
      toast({
        title: 'CSV downloaded',
        description: `${section.reportLabel} • ${formatDateRangeHuman(fromISO, toISO)}`,
      })
    } catch (e) {
      toast({
        title: 'Export failed',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      })
    } finally {
      setBusy(false)
    }
  }

  const handleExcel = async (section: ReportExportSection) => {
    setBusy(true)
    try {
      await apiClient.exportReportXlsx(section.report, fromISO, toISO, section.extraParams ?? {})
      toast({
        title: 'Excel downloaded',
        description: `${section.reportLabel} • ${formatDateRangeHuman(fromISO, toISO)}`,
      })
    } catch (e) {
      toast({
        title: 'Export failed',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={busy} className="gap-2">
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Download className="w-3.5 h-3.5" />
          )}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {sections.map((section, i) => (
          <Fragment key={section.report}>
            {i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs">Export {section.reportLabel}</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleCsv(section)}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              CSV file
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExcel(section)}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel (.xlsx)
            </DropdownMenuItem>
            {section.onPrintPdf && (
              <DropdownMenuItem onClick={() => section.onPrintPdf?.()}>
                <FileText className="w-4 h-4 mr-2" />
                Print / Save as PDF
              </DropdownMenuItem>
            )}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
